import React from 'react';
import SectionCard from '../controls/SectionCard';
import Row from '../controls/Row';
import Select from '../controls/Select';
import NumberInput from '../controls/NumberInput';
import Toggle from '../controls/Toggle';
import Segmented from '../controls/Segmented';
import ColorPanel from '../../ColorPanel/ColorPanel';
import { fontOptions } from '../../../utils/fontCatalog';
import { normalizePaint } from '../../../utils/paintUtils';

const TextStyleSection = ({
  model,
  brandColors,
  palette,
  onUsePaint,
  onRequestEyedropper,
  disabled,
}) => {
  const fontFamily = model.mixed('fontFamily');
  const fontSize = model.mixed('fontSize');
  const fontWeight = model.mixed('fontWeight');
  const fontStyle = model.mixed('fontStyle');
  const underline = model.mixed('underline');
  const paint = model.mixed('style.paint');
  const align = model.mixed('align');

  const handleToggleBold = () => {
    const nextValue = fontWeight.kind === 'mixed'
      ? 700
      : fontWeight.value >= 600
        ? 400
        : 700;
    model.applyToSelection({ fontWeight: nextValue }, 'immediate', 'font-weight');
  };

  const handleToggleItalic = () => {
    const nextValue = fontStyle.kind === 'mixed'
      ? 'italic'
      : fontStyle.value === 'italic'
        ? 'normal'
        : 'italic';
    model.applyToSelection({ fontStyle: nextValue }, 'immediate', 'font-style');
  };

  const handleToggleUnderline = () => {
    const nextValue = underline.kind === 'mixed'
      ? true
      : !underline.value;
    model.applyToSelection({ underline: nextValue }, 'immediate', 'underline');
  };

  return (
    <SectionCard title="Typography">
      <Row label="Font">
        <Select
          value={fontFamily.value}
          mixed={fontFamily.kind === 'mixed'}
          options={fontOptions}
          onChange={(value) => model.applyToSelection({ fontFamily: value }, 'debounced', 'font-family')}
          disabled={disabled}
        />
      </Row>
      <Row label="Size">
        <NumberInput
          value={fontSize.value}
          mixed={fontSize.kind === 'mixed'}
          min={6}
          max={300}
          onCommit={(value) => model.applyToSelection({ fontSize: value }, 'debounced', 'font-size')}
          disabled={disabled}
        />
      </Row>
      <Row label="Style">
        <Toggle
          checked={fontWeight.value >= 600}
          indeterminate={fontWeight.kind === 'mixed'}
          onChange={handleToggleBold}
          label="Bold"
          disabled={disabled}
        />
        <Toggle
          checked={fontStyle.value === 'italic'}
          indeterminate={fontStyle.kind === 'mixed'}
          onChange={handleToggleItalic}
          label="Italic"
          disabled={disabled}
        />
        <Toggle
          checked={Boolean(underline.value)}
          indeterminate={underline.kind === 'mixed'}
          onChange={handleToggleUnderline}
          label="Underline"
          disabled={disabled}
        />
      </Row>
      <Row label="Color">
        <ColorPanel
          label="Text colour"
          value={paint.value}
          mixed={paint.kind === 'mixed'}
          allowGradient
          documentColors={palette?.documentColors}
          recentColors={palette?.recentColors}
          recentGradients={palette?.recentGradients}
          brandColors={brandColors}
          onChange={(nextPaint) => {
            const normalized = normalizePaint(nextPaint, '#111111');
            model.applyToSelection((el) => ({
              style: { paint: normalized },
              color: normalized.type === 'solid' ? normalized.color : el.color,
            }), 'debounced', 'text-color');
          }}
          onCommit={(nextPaint) => {
            const normalized = normalizePaint(nextPaint, '#111111');
            model.applyToSelection((el) => ({
              style: { paint: normalized },
              color: normalized.type === 'solid' ? normalized.color : el.color,
            }), 'debounced', 'text-color');
          }}
          onUsePaint={onUsePaint}
          onRequestEyedropper={
            onRequestEyedropper
              ? () => onRequestEyedropper((nextPaint) => {
                const normalized = normalizePaint(nextPaint, '#111111');
                model.applyToSelection((el) => ({
                  style: { paint: normalized },
                  color: normalized.type === 'solid' ? normalized.color : el.color,
                }), 'debounced', 'text-color');
              })
              : undefined
          }
          disabled={disabled}
        />
      </Row>
      <Row label="Align">
        <Segmented
          value={align.value}
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ]}
          onChange={(value) => model.applyToSelection({ align: value }, 'immediate', 'text-align')}
          disabled={disabled}
        />
      </Row>
    </SectionCard>
  );
};

export default TextStyleSection;
